/** English strings for the ops area. The Vietnamese file must have exactly the same keys. */
export const ops = {
  crumb: 'Operations',
  shared: {
    updated: 'Updated {{time}}',
    run: 'Run',
    runHint: 'Ctrl+Enter runs the query',
    reasonHint: 'This query names a business object; a reason will be required.',
    open: 'Open',
    none: 'None',
    rawResponse: 'Raw response',
    withheld: '{{label}}: withheld. Viewing payloads needs a permission you do not have.',
    empty: '{{label}}: none.',
    loadFailed: 'Could not load this data',
    notConfigured: 'Not configured in this environment',
    showAll: 'Show all ({{count}})',
    showLess: 'Show fewer',
    clearFilter: 'Clear',
  },
  status: { ok: 'Healthy', warn: 'Warning', crit: 'Critical', unknown: 'No data' },
  severity: { P0: 'Critical', P1: 'High', P2: 'Medium', P3: 'Info' },
  incidentStatus: { OPEN: 'Open', ACKNOWLEDGED: 'Acknowledged', MITIGATING: 'Mitigating', MONITORING: 'Monitoring', RESOLVED: 'Resolved' },
  workflowState: {
    PENDING: 'Pending',
    RUNNING: 'Running',
    WAITING: 'Waiting',
    RETRYING: 'Retrying',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    MANUAL_REVIEW: 'Manual review',
    CANCELLED: 'Cancelled',
    COMPENSATING: 'Compensating',
  },
  stepOutcome: { STARTED: 'Started', COMPLETED: 'Completed', FAILED: 'Failed', RETRY: 'Retry', WAITING: 'Waiting', UNKNOWN: 'Unknown', SKIPPED: 'Skipped' },
  commandState: {
    PENDING: 'Pending',
    SENT: 'Sent',
    SUCCESS: 'Success',
    REJECTED: 'Rejected',
    UNKNOWN: 'Unknown',
    RECONCILING: 'Reconciling',
    RETRY_PENDING: 'Retry pending',
    MANUAL_REVIEW: 'Manual review',
  },
  auditOutcome: { SUCCESS: 'Success', DENIED: 'Denied', FAILURE: 'Failure', ERROR: 'Error' },
  eventStatus: { PUBLISHED: 'Published', PENDING: 'Pending', FAILED: 'Failed' },
  alertState: { active: 'Firing', suppressed: 'Silenced', unprocessed: 'Unprocessed' },
  gap: {
    MISSING_DOSSIER: 'Missing dossier',
    INCOMPLETE_DOSSIER: 'Incomplete dossier',
    VERIFICATION_NOT_VALID: 'Verification not valid',
    SUBJECT_MISMATCH: 'Subject mismatch',
    VNNIC_SYNC_MISSING: 'Sync missing',
    VNNIC_SYNC_PENDING: 'Sync pending',
    VNNIC_SYNC_REJECTED: 'Sync rejected',
    VNNIC_SYNC_ERROR: 'Sync error',
    REPORT_PROJECTION_MISSING: 'Report projection missing',
    VNNIC_REGISTRY_MISSING: 'Not on VNNIC list',
  },

  overview: {
    title: 'Operations overview',
    subtitle: 'Registry operations, money, compliance and platform health at a glance.',
    summary: {
      crit_one: '{{count}} critical',
      crit_other: '{{count}} critical',
      warn_one: '{{count}} warning',
      warn_other: '{{count}} warnings',
      unknown_one: '{{count}} without data',
      unknown_other: '{{count}} without data',
      healthy: 'All indicators healthy',
      label: 'Health summary',
    },
    updated: 'Updated {{time}}',
    updating: 'Updating…',
    staleHint: 'The last refresh failed; showing the previous data.',
    autoRefresh: 'Auto-refresh 30 s',
    autoRefreshHint: 'Reload every 30 seconds while this tab is visible',
    attention: {
      title: 'Needs attention now',
      description: 'Most severe first. Each item links to where it is resolved.',
      emptyTitle: 'Nothing needs attention',
      emptyBody: 'No firing alerts, open incidents, down services, exhausted error budgets or indicators past their thresholds.',
      source: { alert: 'Alert', incident: 'Incident', indicator: 'Indicator', slo: 'SLO', service: 'Service' },
      alertDetail: '{{name}} · started {{ago}}',
      incidentDetail: '{{status}} · started {{ago}}',
      serviceDown: '{{service}}: {{down}} of {{total}} instances down',
      serviceDetail: 'These instances stopped answering health checks.',
      sloExhausted: '{{name}}: error budget exhausted',
      sloLow: '{{name}}: {{remaining}} of the error budget left',
      sloDetail: 'Objective {{objective}} · current {{current}} · window {{window}}',
      act: 'Open',
    },
    group: {
      registry: 'Registry & domains',
      registryHint: 'Registry commands, reconciliation and workflows that need an operator.',
      money: 'Money',
      moneyHint: 'Double-entry ledger invariant.',
      compliance: 'Compliance',
      complianceHint: 'VNNIC coverage of managed domains.',
      response: 'Incident response',
      responseHint: 'Firing alerts and the incident log.',
      platform: 'Platform',
      platformHint: 'Services, queues, outbox and objectives.',
    },
    tile: {
      ledger: {
        label: 'Ledger integrity',
        caption: 'Unbalanced journal entries (must be 0)',
        alert: 'The double-entry invariant is broken. Page billing on-call; do not post corrections by hand.',
        link: 'Open ledger',
      },
      unknown: {
        label: 'Unresolved registry outcomes',
        caption: 'UNKNOWN, reconciling or manual review · {{open}} open in total',
        alert: 'Resolved by reconciliation against registry state, never by a blind retry.',
        link: 'Registry health',
      },
      recon: {
        label: 'Reconciliation backlog',
        caption: 'Open reconciliation issues',
        alert: 'Registry and local state disagree on these objects.',
        link: 'Reconciliation',
      },
      workflows: {
        label: 'Workflows in manual review',
        caption: '{{inFlight}} workflows in flight',
        alert: 'Manual review needs an operator decision.',
        link: 'Workflows',
      },
      queues: {
        label: 'Ready jobs',
        caption: 'Oldest waiting since {{age}} · {{dead}} dead',
        captionIdle: 'Nothing waiting · {{dead}} dead',
        alertDead_one: '{{count}} job exhausted its retries.',
        alertDead_other: '{{count}} jobs exhausted their retries.',
        alertOld: 'The oldest ready job has waited more than 15 minutes.',
        link: 'Queues',
      },
      outbox: {
        label: 'Outbox pending',
        caption: 'Events not yet published',
        alert: 'Event publishing is falling behind.',
        link: 'Outbox',
      },
      compliance: {
        label: 'VNNIC compliance gaps',
        caption: 'Managed domains not fully covered',
        captionNone: 'Every managed domain is covered',
        alert: 'Domains are missing a dossier, verification or VNNIC sync.',
        link: 'Coverage',
      },
      incidents: {
        label: 'Open incidents',
        caption: 'Not yet resolved',
        alert: 'An incident is in progress.',
        link: 'Incidents',
      },
      alerts: {
        label: 'Firing alerts',
        caption: '{{silenced}} silenced or inhibited',
        link: 'Alerts',
      },
      services: {
        label: 'Service instances up',
        caption: '{{services}} services monitored',
        link: 'Services',
      },
      slo: {
        label: 'SLOs within objective',
        caption: '{{low}} with less than 25% error budget left',
        link: 'SLOs',
      },
    },
    queueDepth: {
      title: 'Job queue depth',
      description: 'Jobs per queue right now; scheduled jobs are counted separately.',
      ready: 'Ready',
      running: 'Running',
      retrying: 'Retrying',
      dead: 'Dead',
      scheduled: '+{{count}} scheduled',
      rowLabel: '{{queue}}: {{ready}} ready, {{running}} running, {{retrying}} retrying, {{dead}} dead',
      empty: 'No job queues reported',
    },
    inFlight: {
      title: 'Work in flight',
      description: 'Open registry transactions and workflows by state.',
      transactions: 'Registry transactions',
      workflows: 'Workflows',
      empty: 'Nothing in flight',
    },
    budgets: {
      title: 'Error budget remaining',
      description: 'Lowest first, over each SLO window.',
      empty: 'No SLOs defined',
    },
    registries: {
      title: 'Registries',
      description: 'Open work and the last command outcome per registry.',
      open: '{{count}} open',
      unknown: '{{count}} unknown',
      lastSuccess: 'last success {{ago}}',
      never: 'no success recorded',
      empty: 'No registries configured',
    },
    footnote: 'These are the values as of the last refresh, not a live stream, and they can be up to 15 seconds old. Trends are under Metrics, objectives under SLOs.',
    loadFailed: 'Could not load the overview',
  },

  chart: {
    truncated: 'Showing the first {{shown}} of {{total}} series; the rest are in the table. Aggregate with sum by (…) to reduce them.',
  },

  services: {
    title: 'Services',
    subtitle: 'Every platform service and whether each of its instances is answering.',
    filter: 'Service',
    filterPlaceholder: 'Filter by name…',
    onlyProblems: 'Only services with a down instance',
    stat: { services: 'Services', up: 'Instances up', down: 'Instances down', allUp: 'Every instance answers', downHint: 'Not answering health checks' },
    col: { service: 'Service', health: 'Health', instances: 'Instances' },
    health: '{{up}}/{{total}} up',
    down: 'down',
    up: 'up',
    unlabelled: '(unnamed)',
    empty: 'No service is reporting',
    noMatch: 'No service matches this filter',
    loadFailed: 'Could not load services',
    detail: {
      subtitle: 'Instances, what is firing and which objectives cover this service.',
      notFound: 'No service named {{name}} is reporting',
      stat: { instances: 'Instances up', alerts: 'Alerts firing', incidents: 'Open incidents' },
      instances: 'Instances',
      col: { instance: 'Instance', state: 'State' },
      instancesEmpty: 'No instance is reporting for this service',
      alerts: 'Alerts for this service',
      alertsEmpty: 'Nothing is firing for this service',
      incidents: 'Incidents naming this service',
      incidentsEmpty: 'No incident names this service',
      slos: 'Objectives covering this service',
      slosEmpty: 'No objective covers this service',
      budgetLeft: '{{value}} of the error budget left',
      logs: 'Logs',
      metrics: 'Request rate',
      traces: 'Traces',
    },
  },

  registries: {
    title: 'Registry health',
    subtitle: 'Open and unresolved registry transactions per registry.',
    badge: { ok: 'Healthy', warn: 'Issues open', crit: 'Unknown outcomes' },
    field: {
      open: 'Open transactions',
      unknown: 'Unknown / reconciling',
      issues: 'Open issues',
      lastSuccess: 'Last success',
      lastFailure: 'Last failure',
    },
    latest: 'most recent',
    eppLatency: 'EPP p95 latency',
    logs: 'Logs',
    traces: 'Traces',
    note: 'How fast a registry answers is outside our control; the time our own handling adds has its own objective under SLOs.',
    empty: 'No registries configured',
    loadFailed: 'Could not load registries',
  },

  queues: {
    title: 'Queues',
    subtitle: 'Job queues, the outbox and in-flight workflows.',
    stat: { ready: 'Ready jobs', dead: 'Dead jobs', outbox: 'Outbox pending', workflows: 'Workflows in flight', deadHint: 'Retries exhausted; needs a decision', readyHint: 'Waiting for a worker' },
    jobs: { title: 'Scheduled actions (job queues)', empty: 'No queues' },
    outbox: { title: 'Outbox (pending events)', empty: 'Nothing pending' },
    workflows: { title: 'Workflows in flight', empty: 'No workflows in flight' },
    col: {
      queue: 'Queue',
      ready: 'Ready',
      running: 'Running',
      scheduled: 'Scheduled',
      retrying: 'Retrying',
      dead: 'Dead',
      oldestReady: 'Oldest ready',
      maxAttempts: 'Max attempts',
      eventType: 'Event type',
      pending: 'Pending',
      oldest: 'Oldest',
      workflowType: 'Workflow type',
      state: 'State',
      count: 'Count',
      oldestInState: 'Oldest in state',
    },
    loadFailed: 'Could not load queues',
  },

  metrics: {
    title: 'Metrics',
    subtitle: 'Query platform metrics. Every query is recorded in the audit log.',
    promql: 'Query',
    mode: 'Query type',
    range: 'Range',
    instant: 'Instant',
    step: 'Step (s)',
    stepAuto: 'auto',
    instantHint: 'Instant queries evaluate at now, or at the end of a custom range.',
    examplesTitle: 'Examples',
    example: {
      http5xx: 'HTTP 5xx ratio by service',
      eppP95: 'EPP p95 latency by registry',
      eppCommands: 'EPP commands by operation',
      workflows: 'Workflows by state',
      outbox: 'Outbox pending',
      ledger: 'Ledger imbalance',
    },
    emptyTitle: 'Enter a query',
    emptyBody: 'Pick an example above or write your own. Ranges are capped at 30 days and 11,000 points.',
    querying: 'Querying…',
    failed: 'Query failed',
    emptyResult: 'Empty result',
    resultTitle: 'Result',
    col: { series: 'Series', value: 'Value', min: 'Min', max: 'Max', last: 'Last' },
    tableView_one: 'Table view ({{count}} series)',
    tableView_other: 'Table view ({{count}} series)',
  },

  logs: {
    title: 'Logs',
    subtitle: 'Search logs or follow them live. Personal data is removed before lines reach this screen, and every search is recorded in the audit log.',
    logql: 'Query',
    liveTail: 'Live tail',
    limit: 'Limit',
    builder: {
      title: 'Build a query from filters',
      service: 'Service',
      level: 'Level',
      any: 'Any',
      registry: 'Registry',
      operation: 'Operation',
      traceId: 'Trace ID',
      requestId: 'Request ID',
      correlationId: 'Correlation ID',
      errorCode: 'Error code',
      text: 'Line contains',
      use: 'Use this query',
    },
    row: { trace: 'Trace', correlation: 'Correlation', context: 'Context ±1 min', raw: 'Raw line' },
    tail: {
      pause: 'Pause',
      resume: 'Resume',
      status_one: 'Polling every {{interval}} s for the last {{window}} s · {{count}} line kept (max {{max}})',
      status_other: 'Polling every {{interval}} s for the last {{window}} s · {{count}} lines kept (max {{max}})',
      running: 'Running',
      paused: 'Paused',
      stopped: 'Live tail stopped',
      waiting: 'Waiting for lines…',
    },
    emptyTitle: 'Enter a query',
    emptyBody: 'Start with a stream selector such as {{example}}, or build one from filters.',
    searching: 'Searching logs…',
    failed: 'Log query failed',
    result_one: '{{count}} line, newest first',
    result_other: '{{count}} lines, newest first',
    limitReached: 'limit {{limit}} reached',
    scrubbed: 'personal data removed',
    range: '{{start}} to {{end}}',
    noLines: 'No lines in this range',
    resultTitle: 'Results',
  },

  traces: {
    title: 'Traces',
    subtitle: 'Follow one request across services. Only approved attributes can be searched.',
    jumpTitle: 'Open a trace by ID',
    jump: 'Trace ID',
    jumpPlaceholder: '32 hex characters',
    searchTitle: 'Search by attributes',
    attribute: 'Attribute {{n}}',
    value: 'Value {{n}}',
    remove: 'Remove',
    addAttribute: 'Add attribute',
    search: 'Search',
    emptyTitle: 'Search by attribute',
    emptyBody: 'For example registry = VNNIC, or a correlation ID.',
    searching: 'Searching traces…',
    failed: 'Trace search failed',
    noTraces: 'No traces in this range',
    col: { trace: 'Trace', root: 'Root span', started: 'Started', duration: 'Duration' },
  },

  trace: {
    title: 'Trace',
    crumb: 'Traces',
    invalid: '{{id}} is not a trace ID. Trace IDs are 32 hexadecimal characters.',
    loading: 'Loading trace…',
    notFound: 'Trace not found (it may have aged out of retention)',
    failed: 'Could not load the trace',
    noSpans: 'No spans in the response',
    noSpansBody: 'This trace came back in a format this view does not recognise.',
    field: { root: 'Root', started: 'Started', duration: 'Duration', spans: 'Spans', services: 'Services', errors: 'Errors' },
    logsFor: 'Logs for this trace',
    correlation: 'Correlation (events, workflow, registry)',
    tab: { waterfall: 'Waterfall', raw: 'Raw JSON' },
    waterfall: {
      label: 'Spans',
      span: 'Span',
      timeline: 'Timeline ({{total}})',
      error: 'error',
      spanId: 'Span ID',
      parent: 'Parent',
      root: 'root',
      kind: 'Kind',
      startOffset: 'Start offset',
      duration: 'Duration',
      status: 'Status',
    },
  },

  correlation: {
    title: 'Correlation',
    invalid: '{{id}} is not a correlation ID (a UUID).',
    loading: 'Correlating…',
    failed: 'Could not correlate',
    follow: {
      logs: 'Open in log explorer',
      traces: 'Search traces',
      history: '{{domain}} history',
    },
    emptyTitle: 'Nothing recorded under this correlation ID',
    emptyBody: 'No domain events, workflows, registry transactions, audit rows or published events carry it. Logs and traces may still.',
    tab: {
      events: 'Domain events',
      workflows: 'Workflows',
      transactions: 'Registry transactions',
      audit: 'Audit',
      published: 'Published events',
      logs: 'Logs',
    },
    col: {
      when: 'When',
      domain: 'Domain',
      sequence: '#',
      event: 'Event',
      request: 'Request',
      workflow: 'Workflow',
      state: 'State',
      step: 'Step',
      failure: 'Failure',
      resource: 'Resource',
      started: 'Started',
      operation: 'Operation',
      trid: 'clTRID / svTRID',
      result: 'Result',
      completed: 'Completed',
      action: 'Action',
      actor: 'Actor',
      outcome: 'Outcome',
      created: 'Created',
      eventType: 'Event type',
      status: 'Status',
    },
    noSvtrid: 'no svTRID',
    logs: {
      loading: 'Reading the logs…',
      failed: 'Could not read the logs of this flow',
      summary_one: '{{count}} line, oldest first (the order the flow ran)',
      summary_other: '{{count}} lines, oldest first (the order the flow ran)',
      window: 'From {{start}} to {{end}}: the earliest to the latest record of the flow, 15 minutes either side.',
      fallback: 'Nothing recorded to scope the search by: the last 7 days, {{start}} to {{end}}.',
      none: 'No log line carries this correlation ID in that time.',
      older: 'Load older lines',
      capped: 'Showing the newest {{limit}} lines of each search; older ones may remain.',
      query: 'Query run:',
    },
    exchange: {
      title: 'Exchanged text',
      hint: 'request and response as sent to and received from the registry; read from the log store and decrypted when opened, each view is audited',
      group: 'Registry transaction',
      ungrouped: 'Registry exchange (no transaction recorded)',
      noCltrid: 'This line carries no readable clTRID, so its exchanged text cannot be opened.',
    },
  },

  alerts: {
    title: 'Alerts',
    subtitle: 'What is firing now, grouped, with one click to open an incident.',
    groupBy: 'Group by',
    group: { severity: 'Severity', alertname: 'Alert', service: 'Service' },
    showSilenced: 'Show silenced / inhibited',
    readOnly: 'Alerts are read-only here: silencing and acknowledging them happens in the alerting tool. Opening an incident from an alert is done on this page.',
    stat: { firing: 'Firing', urgent: 'P0 / P1', silenced: 'Silenced / inhibited', urgentHint: 'Page on-call', silencedHint: 'Suppressed, but the condition still holds' },
    emptyTitle: 'No alerts firing',
    emptyBody: 'Nothing is firing right now.',
    since: 'started {{ago}}',
    openIncident: 'Open incident',
    runbook: 'Runbook',
    runbookPath: 'Runbook:',
    loadFailed: 'Could not load alerts',
    detail: {
      subtitle: 'Every instance of this alert, its runbook and the incidents that name it.',
      firing: 'Firing now',
      firingEmpty: 'This alert is not firing right now',
      stat: { firing: 'Firing', silenced: 'Silenced / inhibited', severity: 'Severity' },
      services: 'Services affected',
      incidents: 'Incidents naming this alert',
      incidentsEmpty: 'No incident names this alert',
    },
  },

  incidents: {
    title: 'Incidents',
    subtitle: 'Every incident, unresolved first. Opening one and moving it forward needs incident-management permission.',
    open: 'Open incident',
    filter: { status: 'Status', active: 'Unresolved', resolved: 'Resolved', all: 'All' },
    col: { severity: 'Sev', title: 'Title', status: 'Status', owner: 'Owner', started: 'Started' },
    empty: 'No incidents',
    emptyFiltered: 'No incidents with this status',
    openHint: 'Open an incident to see its timeline and move it forward.',
    loadFailed: 'Could not load incidents',
    form: {
      title: 'Open an incident',
      description: 'Starts the incident clock now. Link the alerts and services involved so responders can follow them.',
      severity: 'Severity',
      titleLabel: 'Title',
      relatedAlerts: 'Related alerts',
      relatedServices: 'Related services',
      commaHint: 'Comma-separated',
      opening: 'Opening…',
      submit: 'Open incident',
    },
    move: {
      button: 'Change status',
      title: 'Move incident',
      description: 'Incidents move forward only (monitoring may return to mitigating).',
      newStatus: 'New status',
      rootCause: 'Root cause',
      resolution: 'Resolution',
      recommended: 'recommended',
      optional: 'optional',
      requiredToResolve: 'required to resolve',
      saving: 'Saving…',
      submit: 'Move to {{status}}',
      closed: 'This incident is resolved; no further transitions.',
    },
    detail: {
      tabOverview: 'Overview',
      tabAnalysis: 'Root cause & resolution',
      subtitle: 'Started {{ago}}',
      status: 'Status',
      started: 'Started',
      acknowledged: 'Acknowledged',
      resolved: 'Resolved',
      duration: 'Duration',
      owner: 'Owner',
      id: 'Incident ID',
      relatedAlerts: 'Related alerts',
      relatedServices: 'Related services',
      rootCause: 'Root cause',
      resolution: 'Resolution',
      notRecorded: 'Not recorded yet',
      notFound: 'Incident not found',
      loadFailed: 'Could not load the incident',
      timeline: 'Timeline',
      timelineEmpty: 'No change recorded yet',
      opened: 'Opened',
      movedTo: 'Moved to {{status}}',
      by: 'by {{who}}',
      reason: 'Reason',
      alertsEmpty: 'No alert is linked to this incident',
      servicesEmpty: 'No service is linked to this incident',
      domainsNote: 'An incident records the services it affects, not individual domains. Follow an affected service into its logs and traces to find the domains behind it.',
    },
  },

  slo: {
    title: 'Service level objectives',
    subtitle: 'What we promise per service, what we are delivering, and how much error budget is left.',
    stat: { total: 'SLOs', meeting: 'Meeting objective', low: 'Budget ≤ 25%', exhausted: 'Budget exhausted', lowHint: 'Prioritise reliability work', exhaustedHint: 'Feature releases freeze' },
    col: { slo: 'SLO', service: 'Service', objective: 'Objective', current: 'Current', budget: 'Error budget remaining', window: 'Window' },
    queryFailed: 'measurement failed',
    noBackend: 'not measured here',
    exhausted: 'Exhausted',
    meterLabel: 'Error budget remaining for {{name}}',
    policyTitle: 'Budget policy',
    policy: 'At 25% or less remaining, reliability work is prioritised; when the budget is exhausted, feature releases for that service freeze. The current figure is measured over the SLO window each time this page is opened.',
    empty: 'No SLOs defined',
    loadFailed: 'Could not load SLOs',
  },

  workflows: {
    title: 'Workflows',
    subtitle: 'Long-running operations and how far each one has got, newest first.',
    filter: {
      status: 'Status',
      any: 'Any',
      active: 'Active (pending / running / waiting / retrying)',
      type: 'Type',
      resource: 'Resource',
      correlation: 'Correlation',
    },
    col: {
      workflow: 'Workflow',
      status: 'Status',
      step: 'Current step',
      resource: 'Resource',
      attempt: 'Attempt',
      failure: 'Failure',
      created: 'Created',
      updated: 'Updated',
      correlation: 'Correlation',
    },
    leased: 'leased',
    leasedBy: 'Leased by {{worker}}',
    empty: 'No workflows match',
    loadFailed: 'Could not load workflows',
  },

  workflow: {
    title: 'Workflow',
    crumb: 'Workflows',
    notFound: 'Workflow not found',
    loadFailed: 'Could not load the workflow',
    tab: { instance: 'Instance', steps: 'Steps ({{count}})', commands: 'Registry commands ({{count}})', payload: 'Input and context' },
    instanceTitle: 'Instance',
    field: {
      state: 'State',
      step: 'Current step',
      businessKey: 'Business key',
      attempt: 'Attempt',
      resource: 'Resource',
      nextRun: 'Next run',
      lease: 'Held by worker',
      leaseValue: '{{worker}} until {{until}}',
      leaseNone: 'no worker holds it',
      failure: 'Failure',
      created: 'Created',
      updated: 'Updated',
      completed: 'Completed',
      version: 'Version',
    },
    follow: {
      title: 'Follow it',
      correlation: 'Correlation',
      request: 'Request',
      causedBy: 'Caused by event',
      logs: 'Logs for this operation',
      traces: 'Traces for this operation',
    },
    steps: {
      empty: 'No step has run yet',
      attempt: 'attempt {{n}}',
      started: 'started {{at}}',
      worker: 'worker',
      trace: 'trace',
      output: 'Output (redacted)',
    },
    commands: { empty: 'This workflow issued no registry command' },
    payload: { note: 'Secrets are removed and personal identifiers masked before these leave the server.', input: 'Input', context: 'Context' },
  },

  retention: {
    title: 'Log retention',
    subtitle: 'How many days each kind of log stays in the log store before it is deleted.',
    note: 'Every service, worker, web server, database and message bus log goes to the log store. The exchanges with registries and registrars (EPP and REST requests and answers) are kept apart, for their own number of days. Business records (registry commands, the audit log) stay in the database and are not affected. Older logs are cleared every hour; the log store finishes a deletion within a day.',
    section: 'Days kept',
    registry_exchange_days: { label: 'Exchanges with registries and registrars', hint: 'EPP and REST requests and answers of every command, read or write. {{min}} to {{max}} days.' },
    other_days: { label: 'Every other log', hint: 'Services, workers, web server, proxy, database, message bus. {{min}} to {{max}} days.' },
    reason: 'Reason for the change',
    save: 'Save',
    readOnly: 'Only operations administrators can change this.',
    updated: 'Last changed {{at}}',
    defaults: 'Default values, never changed.',
  },
} as const;
